import moment from 'moment'
import type { GraphSeries } from 'app/components/viz/graphs/AreaSeriesGraph'
import { formatDate } from 'app/date.utils'
import type { QualifiedLeadsMetrics, Row } from 'app/types/qualified-leads'
import { divide } from 'app/utils'

/**
 * Sums a daily schedule-rate series into week or month buckets for the graph.
 *
 * The bucket key is the ISO date (`YYYY-MM-DD`) of the UTC start of the group, so every later
 * parse of it is a format moment reads without the RFC2822/ISO deprecation fallback.
 */
export function groupScheduleRateSeries(
  series: QualifiedLeadsMetrics[`series`],
  resolvedGroup: `weeks` | `months`,
): GraphSeries[] {
  let seriesGroupBydate: Row[] = series
    .reduce((acc: Row[] = [], serie) => {
      let group = moment.utc(serie.date).startOf(resolvedGroup).format(`YYYY-MM-DD`)

      let index = acc.findIndex((i) => i.date === group)
      if (index !== -1) {
        let currentGroup = acc[index]
        currentGroup.scheduled = currentGroup.scheduled + serie.scheduled
        currentGroup.leads = currentGroup.leads + serie.leads
        return acc
      } else {
        return acc.concat({
          date: group,
          scheduled: serie.scheduled,
          leads: serie.leads,
          scheduledRate: 0,
        })
      }
    }, [])
    .map((serie) => ({
      ...serie,
      scheduledRate: Math.round(divide(serie.scheduled, serie.leads) * 100),
    }))

  return seriesGroupBydate.map((datum) => ({
    ...datum,
    date:
      resolvedGroup === `weeks`
        ? (formatDate({ value: datum.date, dateOnly: true, format: `MMM D` }) ?? datum.date)
        : (formatDate({ value: datum.date, dateOnly: true, format: `MMM YYYY` }) ?? datum.date),
    tooltip:
      resolvedGroup === `weeks`
        ? `${formatDate({ value: datum.date, dateOnly: true, format: `MMM D` })} - ${moment.utc(datum.date).endOf(`week`).format(`MMM D`)}`
        : formatDate({ value: datum.date, dateOnly: true, format: `MMM YYYY` }) || datum.date,
  }))
}
